import { KaboodleToaster } from "components/common";
import { useCallback, useMemo } from "react";
import type { DropEvent, FileRejection } from "react-dropzone";
import { useDropzone } from "react-dropzone";
import { getSessionId, logToSentry } from "utils/sentry";

import {
  getAcceptedMimeTypes,
  getBlankDocumentMessage,
  isLikelyBlankDocument,
  isValidFileExtension,
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_DISPLAY,
} from "../DocumentConstants";

interface UseDocumentDropzoneProps {
  onDrop: (acceptedFiles: File[]) => void;
  noClick?: boolean;
  noKeyboard?: boolean;
  disabled?: boolean;
  // When true, .mp4 files are accepted (Organization.allow_mp4_uploads). Callers
  // in the authenticated app read this from useGetOrganizationSettingsQuery; the
  // intake portal has no org-settings access, so it stays false there.
  allowMp4?: boolean;
}

interface FileValidationResult {
  validFiles: File[];
  hasRejectedFiles: boolean;
}

/**
 * custom hook that wraps react-dropzone with our document validation logic
 * uses react-dropzone's built-in isDragActive for overlay state management
 */
export const useDocumentDropzone = ({
  onDrop,
  noClick = true,
  noKeyboard = true,
  disabled = false,
  allowMp4 = false,
}: UseDocumentDropzoneProps) => {
  const acceptedMimeTypes = useMemo(
    () => getAcceptedMimeTypes(allowMp4),
    [allowMp4],
  );

  // react-dropzone's default file aggregator (file-selector) extracts dropped
  // files by walking event.dataTransfer.items with webkitGetAsEntry(). That
  // traversal returns an empty list for ordinary file drops in some browsers,
  // so onDrop received zero files and drag-and-drop silently did nothing —
  // while the Upload button (OS dialog, event.target.files) kept working. Read
  // the files straight off the event instead: we don't support folder drops, so
  // the simple path is both sufficient and reliable across browsers.
  const getFilesFromEvent = useCallback(
    async (event: DropEvent): Promise<File[]> => {
      const dataTransfer = (event as DragEvent).dataTransfer;
      if (dataTransfer?.files?.length) {
        return Array.from(dataTransfer.files);
      }
      const target = (event as { target?: HTMLInputElement | null }).target;
      if (target?.files?.length) {
        return Array.from(target.files);
      }
      return [];
    },
    [],
  );

  const validateFiles = useCallback(
    (
      acceptedFiles: File[],
      rejectedFiles?: FileRejection[],
    ): FileValidationResult => {
      // handle rejected files first (from react-dropzone)
      if (rejectedFiles && rejectedFiles.length > 0) {
        rejectedFiles.forEach((rejection) => {
          rejection.errors.forEach((error) => {
            if (error.code === "file-too-large") {
              KaboodleToaster.error(
                `File "${rejection.file.name}" is too large. Maximum file size is ${MAX_FILE_SIZE_DISPLAY}.`,
              );
            } else if (error.code === "file-invalid-type") {
              KaboodleToaster.error(
                `File "${rejection.file.name}" is not a supported file type.`,
              );
            }
          });
        });
      }

      // validate file types for accepted files as well (extra safety)
      const typeValidFiles = acceptedFiles.filter((file: File) => {
        const isValid = isValidFileExtension(file.name, allowMp4);
        if (!isValid) {
          const uiSessionId = getSessionId();
          logToSentry("Document upload - file type not supported", "warn", {
            uiSessionId,
            filename: file.name,
            fileType: file.type,
            context: "main_document_system",
          });
          KaboodleToaster.error(
            `File "${file.name}" is not a supported file type.`,
          );
        }
        return isValid;
      });

      // validate file sizes (extra safety for files that passed dropzone validation)
      const oversizedFiles = typeValidFiles.filter(
        (file) => file.size > MAX_FILE_SIZE,
      );
      if (oversizedFiles.length > 0) {
        const fileNames = oversizedFiles.map((file) => file.name).join(", ");
        const uiSessionId = getSessionId();
        logToSentry("Document upload - file size limit exceeded", "warn", {
          uiSessionId,
          fileNames,
          fileSizes: oversizedFiles.map((file) => file.size),
          context: "main_document_system",
        });
        KaboodleToaster.error(
          `The following file(s) exceed the ${MAX_FILE_SIZE_DISPLAY} size limit: ${fileNames}`,
        );
      }

      // filter out oversized files
      const sizeValidFiles = typeValidFiles.filter(
        (file) => file.size <= MAX_FILE_SIZE,
      );

      // validate that files are not blank or empty
      const blankFiles = sizeValidFiles.filter((file) =>
        isLikelyBlankDocument(file),
      );
      if (blankFiles.length > 0) {
        const uiSessionId = getSessionId();
        blankFiles.forEach((file) => {
          KaboodleToaster.error(getBlankDocumentMessage(file.name));

          // log blank document detection for main document system
          logToSentry("Document upload - blank document detected", "warn", {
            uiSessionId,
            filename: file.name,
            fileType: file.type,
            fileSize: file.size,
            context: "main_document_system",
          });
        });
      }

      // return only files that pass all validation (type, size, and content)
      const validFiles = sizeValidFiles.filter(
        (file) => !isLikelyBlankDocument(file),
      );

      return {
        validFiles,
        hasRejectedFiles: rejectedFiles ? rejectedFiles.length > 0 : false,
      };
    },
    [allowMp4],
  );

  const handleDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      // use our centralized validation logic for all files
      const { validFiles } = validateFiles(acceptedFiles, rejectedFiles);

      // only call onDrop with validated files
      if (validFiles.length > 0) {
        onDrop(validFiles);
      }
    },
    [onDrop, validateFiles],
  );

  const dropzoneProps = useDropzone({
    onDrop: handleDrop,
    getFilesFromEvent,
    accept: acceptedMimeTypes,
    noClick,
    noKeyboard,
    maxSize: MAX_FILE_SIZE,
    disabled,
  });

  // validateFiles is intentionally not returned: it closes over `allowMp4`, so
  // an external consumer that cached it could validate against a stale flag. It
  // has no callers today, so keep it internal to the hook where handleDrop
  // always uses the current closure.
  return dropzoneProps;
};
